import React, { useState } from 'react';
import axios from 'axios';
import './Login.css';
import { API_URL } from '../config/data';

const Login = () => {
  const [loginType, setLoginType] = useState('phone'); // 'phone', 'email'
  const [authMethod, setAuthMethod] = useState(null); // 'password', 'sms'
  const [formData, setFormData] = useState({
    phone: '',
    email: '',
    password: '',
    smsCode: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCodeSending, setIsCodeSending] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Очищаем ошибку при изменении поля
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;
    
    // Удаляем все нецифровые символы
    const digitsOnly = value.replace(/\D/g, '');
    
    // Ограничиваем длину для российского номера (10 цифр)
    const limitedDigits = digitsOnly.slice(0, 10);
    
    // Применяем российскую маску: 999 999 99 99
    if (limitedDigits.length > 0) {
      value = limitedDigits.replace(/(\d{3})(\d{0,3})(\d{0,2})(\d{0,2})/, (match, p1, p2, p3, p4) => {
        let result = p1;
        if (p2) result += ` ${p2}`;
        if (p3) result += ` ${p3}`;
        if (p4) result += ` ${p4}`;
        return result;
      });
    } else {
      value = '';
    }
    
    setFormData(prev => ({
      ...prev,
      phone: value
    }));
  };

  const handleLoginTypeChange = (type) => {
    setLoginType(type);
    setAuthMethod(null);
    setFormData({
      phone: '',
      email: '',
      password: '',
      smsCode: ''
    });
    setErrors({});
  };

  const sendCode = async () => {
    setIsCodeSending(true);
    try {
      const codeData = {
        value: loginType === 'phone' ? `+7${formData.phone.replace(/\D/g, '')}` : formData.email
      };

      const response = await axios.post(`${API_URL}/api/auth/send-code`, codeData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200) {
        console.log('Код отправлен успешно');
      }
    } catch (error) {
      console.error('Ошибка при отправке кода:', error);
      setErrors({ general: 'Ошибка при отправке кода. Попробуйте еще раз.' });
    } finally {
      setIsCodeSending(false);
    }
  };

  const checkAuthMethod = async () => {
    if (loginType === 'phone' && !formData.phone.trim()) {
      setErrors({ phone: 'Введите номер телефона' });
      return;
    }
    if (loginType === 'email' && !formData.email.trim()) {
      setErrors({ email: 'Введите email' });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const response = await axios.post(`${API_URL}/api/auth/check-auth-method`, {
        value: loginType === 'phone' ? `+7${formData.phone.replace(/\D/g, '')}` : formData.email
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = response.data;
      console.log('Auth method response:', data);
      setAuthMethod(data.method); // 'password' или 'sms'
      
      // Если метод авторизации - код, отправляем код
      if (data.method === 'sms') {
        console.log('Отправляем код на', loginType === 'phone' ? formData.phone : formData.email);
        await sendCode();
      }
    } catch (error) {
      console.error('Ошибка при проверке метода авторизации:', error);
      
      if (error.response) {
        // Ошибка от сервера
        const errorData = error.response.data;
        if (errorData.message) {
          setErrors({ general: errorData.message });
        } else {
          setErrors({ general: 'Ошибка при проверке метода авторизации' });
        }
      } else if (error.request) {
        // Ошибка сети
        setErrors({ general: 'Ошибка сети. Проверьте подключение к интернету.' });
      } else {
        // Другая ошибка
        setErrors({ general: 'Произошла непредвиденная ошибка' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Проверяем основное поле (телефон или email)
    if (loginType === 'phone') {
      if (!formData.phone.trim()) {
        newErrors.phone = 'Введите номер телефона';
      } else {
        const phoneDigits = formData.phone.replace(/\D/g, '');
        if (phoneDigits.length < 10) {
          newErrors.phone = 'Введите полный номер телефона';
        }
      }
    }

    if (loginType === 'email') {
      if (!formData.email.trim()) {
        newErrors.email = 'Введите email';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Введите корректный email';
      }
    }

    // Проверяем поле авторизации в зависимости от метода
    if (authMethod === 'password') {
      if (!formData.password.trim()) {
        newErrors.password = 'Введите пароль';
      }
    }

    if (authMethod === 'sms') {
      if (!formData.smsCode.trim()) {
        newErrors.smsCode = 'Введите код из SMS';
      } else if (formData.smsCode.length < 4) {
        newErrors.smsCode = 'Введите полный код';
      }
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Если метод авторизации еще не определен, проверяем его
    if (!authMethod) {
      checkAuthMethod();
      return;
    }
    
    // Если метод определен, валидируем и отправляем форму
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      // Подготавливаем данные для входа согласно API документации
      const loginData = {};
      
      // Добавляем email или phone в зависимости от типа входа
      if (loginType === 'phone') {
        loginData.phone = `+7${formData.phone.replace(/\D/g, '')}`;
      } else {
        loginData.email = formData.email;
      }
      
      // Добавляем password или smsCode в зависимости от метода авторизации
      if (authMethod === 'password') {
        loginData.password = formData.password;
      } else {
        loginData.smsCode = parseInt(formData.smsCode);
      }

      console.log('Отправляем данные входа:', loginData);

      // Отправляем запрос на вход
      const response = await axios.post(`${API_URL}/api/auth/login`, loginData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200) {
        console.log('Успешный вход:', response.data);
        
        // Сохраняем токен в localStorage
        if (response.data.token) {
          localStorage.setItem('authToken', response.data.token);
        }
        
        // Перенаправляем на главную страницу или в личный кабинет
        window.location.href = '/account';
      }
    } catch (error) {
      console.error('Ошибка при входе:', error);
      
      if (error.response) {
        // Ошибка от сервера
        const errorData = error.response.data;
        
        if (errorData.message) {
          setErrors({ general: errorData.message });
        } else if (errorData.errors) {
          // Обрабатываем ошибки валидации
          const serverErrors = {};
          errorData.errors.forEach(err => {
            if (err.field === 'email') {
              serverErrors.email = err.message;
            } else if (err.field === 'phone') {
              serverErrors.phone = err.message;
            } else if (err.field === 'password') {
              serverErrors.password = err.message;
            } else if (err.field === 'smsCode') {
              serverErrors.smsCode = err.message;
            }
          });
          setErrors(serverErrors);
        } else {
          setErrors({ general: 'Ошибка при входе в систему' });
        }
      } else if (error.request) {
        // Ошибка сети
        setErrors({ general: 'Ошибка сети. Проверьте подключение к интернету.' });
      } else {
        // Другая ошибка
        setErrors({ general: 'Произошла непредвиденная ошибка' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderLoginForm = () => {
    return (
      <>
        {/* Основное поле - телефон или email */}
        {loginType === 'phone' ? (
          <div className="login__field">
            <div className="login__phone-container">
              <div className="login__phone-flag">
                <img src="/assets/img/ru.svg" alt="Flag" />
              </div>
              <div className="login__phone-code">
                <span className="login__country-code">+7</span>
              </div>
              <input 
                required 
                data-fls-input="" 
                data-fls-input-mask="+7 (999) 999 - 99 - 99" 
                autoComplete="off" 
                type="tel" 
                name="phone"
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder="999 999 99 99" 
                className={`login__phone-input ${errors.phone ? '--form-error' : ''}`}
                disabled={authMethod !== null}
              />
            </div>
            {errors.phone && <div className="login__error-text">{errors.phone}</div>}
          </div>
        ) : (
          <div className="login__field">
            <input 
              required 
              data-fls-input="" 
              autoComplete="off" 
              type="email" 
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email" 
              className={`input ${errors.email ? '--form-error' : ''}`}
              disabled={authMethod !== null}
            />
            {errors.email && <div className="login__error-text">{errors.email}</div>}
          </div>
        )}

        {/* Дополнительное поле авторизации */}
        {authMethod === 'password' && (
          <div className="login__field">
            <div className="password">
              <input 
                required 
                data-fls-input="" 
                autoComplete="off" 
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Пароль" 
                className={`input ${errors.password ? '--form-error' : ''}`}
              />
              <button 
                type="button"
                className={`password__eye ${showPassword ? 'active' : ''}`}
                onClick={() => setShowPassword(!showPassword)}
              >
                👁
              </button>
            </div>
            {errors.password && <div className="login__error-text">{errors.password}</div>}
          </div>
        )}

        {authMethod === 'sms' && (
          <div className="login__field">
            <input 
              required 
              data-fls-input="" 
              autoComplete="off" 
              type="text" 
              name="smsCode"
              value={formData.smsCode}
              onChange={handleInputChange}
              placeholder={loginType === 'phone' ? 'Код из SMS' : 'Код из письма'} 
              maxLength="4"
              className={`input ${errors.smsCode ? '--form-error' : ''}`}
            />
            {errors.smsCode && <div className="login__error-text">{errors.smsCode}</div>}
            <div className="login__code-info">
              Код отправлен на {loginType === 'phone' ? `номер ${formData.phone}` : formData.email}
            </div>
            <button 
              type="button"
              className="login__resend-button"
              onClick={sendCode}
              disabled={isCodeSending}
            >
              {isCodeSending ? 'Отправляем...' : 'Отправить код повторно'}
            </button>
          </div>
        )}

      </>
    );
  };

  return (
    <div className="wrapper">
      <header data-fls-header-login="" data-fls-header-scroll="" className="header-login">
        <div className="header-login__container">
          <div className="header-login__body">
            <a href="/" className="header-login__logo">
              <img src="/assets/img/logo.svg" alt="Logo" />
            </a>
          </div>
        </div>
      </header>
      <main className="page auth">
        <div className="auth__container">
          <div className="auth__body login">
            <form data-fls-form="" action="#" method="#" data-fls-block="" className="login__content" onSubmit={handleSubmit}>
              <h4 className="login__title login__title--title-25">Вход</h4>
              
              {/* Общая ошибка */}
              {errors.general && (
                <div className="login__error-general">
                  {errors.general}
                </div>
              )}
              
              {/* Переключатель типа входа */}
              <div className="login__tabs">
                <button 
                  type="button"
                  className={`login__tab ${loginType === 'phone' ? 'active' : ''}`}
                  onClick={() => handleLoginTypeChange('phone')}
                >
                  Телефон
                </button>
                <button 
                  type="button"
                  className={`login__tab ${loginType === 'email' ? 'active' : ''}`}
                  onClick={() => handleLoginTypeChange('email')}
                >
                  Email
                </button>
              </div>

              {/* Форма входа */}
              {renderLoginForm()}

              {!authMethod ? (
                <button 
                  type="button" 
                  data-fls-button="" 
                  className="login__button button button--fw"
                  onClick={checkAuthMethod}
                  disabled={isLoading}
                >
                  {isLoading ? 'Проверяем...' : 'Продолжить'}
                </button>
              ) : (
                <>
                  <button 
                    type="submit" 
                    data-fls-button="" 
                    className="login__button button button--fw"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Входим...' :
                     authMethod === 'password' ? 'Войти' :
                     authMethod === 'sms' ? (loginType === 'phone' ? 'Войти по SMS' : 'Войти по коду') : 'Войти'}
                  </button>
                  <button 
                    type="button" 
                    className="login__back-button"
                    onClick={() => {
                      setAuthMethod(null);
                      setFormData(prev => ({
                        ...prev,
                        password: '',
                        smsCode: ''
                      }));
                      setErrors({});
                    }}
                  >
                    ← Назад
                  </button>
                </>
              )}
              
              <a href="/forgot-password" className="login__error">Не могу войти</a>
            </form>
            <a href="/signup" className="login__regist">Регистрация</a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;